import React, { useEffect, useRef, useState } from "react"
import { useClickOutside } from "../../hooks"
import { cn } from "../../../../../lib"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react"
import { useDispatch, useSelector } from "react-redux"
import {
  selectAviaSearchCurrency,
  setAviaSearchParams,
} from "../../../../../app/reducers/book/aviaSearchSlice"
import { CURRENCIES } from "../../../../../variables/common"
import { useTranslation } from "react-i18next"
import { formatDate, getDatesBetween, strToDate } from "../../utils/date/common"
import {
  DATES_FIX_MODE,
  DATES_FLEX_MODE,
  LEFT_CALENDAR,
  MAX_DATES_RANGE,
  RIGHT_CALENDAR,
} from "../../utils/variables/search"

const DateModalCalendar = ({ datesState, isDeparture = true }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const currency = useSelector(selectAviaSearchCurrency)

  const { availableDepart, availableRetur, from, to, mode } = datesState

  const currentDates = isDeparture ? datesState.from : datesState.to
  const safeCurrentDates = Array.isArray(currentDates) ? currentDates : []

  const currencySymbol = CURRENCIES.find((item) => item.code === currency).symbol

  const [currentMonth, setCurrentMonth] = useState(
    safeCurrentDates.length ? strToDate(safeCurrentDates[0]) : new Date()
  )
  const [openMonthPicker, setOpenMonthPicker] = useState(null)
  const [lastClick, setLastClick] = useState({
    date: null,
    timestamp: 0,
  })
  const [selectedDates, setSelectedDates] = useState(safeCurrentDates)
  const [isMouseDown, setIsMouseDown] = useState(false)
  const [clickCount, setClickCount] = useState(0)
  const [dragStartDate, setDragStartDate] = useState(null)
  const [direction, setDirection] = useState(0)

  // tracks whether we're in "selecting" mode (first click done, waiting for second click)
  const [isSelecting, setIsSelecting] = useState(false)

  const datePickerSect = useRef(null)

  useClickOutside(datePickerSect, () => setOpenMonthPicker(false))

  const months = t("calendar.months", { returnObjects: true })
  const weekDays = t("calendar.week_days", { returnObjects: true })

  const priceData = {
    "2025-9": {
      2: 195,
      3: 185,
      4: 195,
      5: 195,
      6: 195,
      7: 195,
      9: 195,
      10: 195,
      11: 195,
      12: 185,
      13: 213,
      14: 289,
      15: 215,
      16: 400,
      17: 384,
      18: 417,
      19: 170,
      20: 820,
      21: 212,
      22: 420,
      23: 378,
      24: 253,
      25: 417,
      26: 333,
      27: 663,
      28: 461,
      29: 240,
      30: 263,
    },
    "2025-10": {
      6: 195,
      11: 213,
      12: 289,
      13: 400,
      14: 384,
      15: 535,
      16: 170,
      17: 663,
      18: 212,
      20: 420,
      21: 378,
      22: 253,
      23: 417,
      24: 333,
      25: 663,
      26: 461,
      27: 240,
      28: 263,
      29: 263,
      30: 263,
      31: 263,
    },
  }

  const highlightedDates = {
    "2025-9": [12, 18, 25],
    "2025-10": [23],
  }

  // Helper to parse DD.MM.YYYY strings into Date objects for correct comparison
  const parseFormattedDate = (dateStr) => {
    const [day, month, year] = dateStr.split(".").map(Number)
    return new Date(year, month - 1, day)
  }

  // Always use today as the minimum selectable date
  const minSelectableDate = (() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  })()

  const isPastDate = (day, date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), day)
    return d < minSelectableDate
  }

  useEffect(() => {
    setIsMouseDown(false)
    setDragStartDate(null)
    setClickCount(0)
    setLastClick({ date: null, timestamp: 0 })
    setIsSelecting(false)
    setSelectedDates(isDeparture ? datesState.from || [] : datesState.to || [])

    if (!isDeparture && mode !== DATES_FIX_MODE) {
      dispatch(setAviaSearchParams({ key: "dates.mode", value: DATES_FIX_MODE }))
    } else {
      dispatch(setAviaSearchParams({ key: "dates.mode", value: DATES_FLEX_MODE }))
    }
  }, [isDeparture])

  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1

    return { daysInMonth, startingDayOfWeek }
  }

  const changeMonth = (delta) => {
    setDirection(delta)
    setCurrentMonth((prev) => {
      const next = new Date(prev)
      next.setMonth(next.getMonth() + delta)
      return next
    })
  }

  const selectMonth = (monthIndex, isLeft) => {
    const newDate = new Date(currentMonth.getFullYear(), monthIndex)

    if (isLeft) {
      setCurrentMonth(newDate)
    } else {
      const previousMonth = new Date(newDate.getFullYear(), newDate.getMonth() - 1)
      setCurrentMonth(previousMonth)
    }

    setOpenMonthPicker(null)
  }

  const handleDateSelect = (isLeft) => {
    setOpenMonthPicker((prev) =>
      prev === (isLeft ? LEFT_CALENDAR : RIGHT_CALENDAR)
        ? null
        : isLeft
          ? LEFT_CALENDAR
          : RIGHT_CALENDAR
    )
  }

  const handleFlexClick = (day, date) => {
    const formattedDate = formatDate(day, date)

    if (!isSelecting) {
      setIsSelecting(true)
      setDragStartDate(formattedDate)
      setSelectedDates([formattedDate])

      dispatch(setAviaSearchParams({ key: "dates.from", value: [formattedDate] }))
      dispatch(setAviaSearchParams({ key: "dates.mode", value: DATES_FLEX_MODE }))
    } else {
      setIsSelecting(false)

      const dates = [dragStartDate, formattedDate].sort()
      const dateRange = getDatesBetween(dates[0], dates[1])
      const finalDates = dateRange.length > MAX_DATES_RANGE ? [dragStartDate] : dateRange

      setSelectedDates(finalDates)
      setDragStartDate(null)

      dispatch(setAviaSearchParams({ key: "dates.from", value: finalDates }))
      dispatch(setAviaSearchParams({ key: "dates.mode", value: DATES_FLEX_MODE }))
    }
  }

  const handleFlexMouseEnter = (day, date) => {
    if (!isSelecting || !dragStartDate) return

    const formattedDate = formatDate(day, date)

    const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)
    const nextMonthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 0)
    const hoveredDate = new Date(date.getFullYear(), date.getMonth(), day)

    if (hoveredDate < currentMonthStart || hoveredDate > nextMonthEnd) return

    const dates = [dragStartDate, formattedDate].sort()
    const dateRange = getDatesBetween(dates[0], dates[1])

    if (dateRange.length > MAX_DATES_RANGE) return

    setSelectedDates(dateRange)
  }

  const handleDay = (day, date) => {
    // Block interaction with past dates
    if (isPastDate(day, date)) return

    const formattedDate = formatDate(day, date)
    const currentTime = Date.now()
    const DOUBLE_CLICK_THRESHOLD = 300

    const newFrom = [...(datesState?.from || [])]
    const newTo = [...(datesState?.to || [])]

    const isDoubleClick =
      lastClick.date === formattedDate && currentTime - lastClick.timestamp < DOUBLE_CLICK_THRESHOLD

    if (isDoubleClick) {
      newFrom[0] = formattedDate
      newTo[0] = formattedDate

      updateDatesState(newFrom, newTo, true)
      setClickCount(0)
      setLastClick({ date: formattedDate, timestamp: currentTime })
      setSelectedDates([formattedDate])
      return
    }

    const nextCount = clickCount + 1

    if (isDeparture) {
      if (nextCount % 2 === 1) {
        newFrom[0] = formattedDate
        if (newTo[0] && parseFormattedDate(formattedDate) > parseFormattedDate(newTo[0])) {
          newTo[0] = ""
        }
      } else {
        if (newFrom[0] && parseFormattedDate(formattedDate) < parseFormattedDate(newFrom[0])) {
          newFrom[0] = formattedDate
          newTo[0] = ""
          setClickCount(1)
          setLastClick({ date: formattedDate, timestamp: currentTime })
          updateDatesState(newFrom, newTo, false)
          setSelectedDates(newFrom)
          return
        }
        newTo[0] = formattedDate
      }
    } else {
      if (nextCount % 2 === 1) {
        newTo[0] = formattedDate
        if (newFrom[0] && parseFormattedDate(formattedDate) < parseFormattedDate(newFrom[0])) {
          newFrom[0] = ""
        }
      } else {
        if (newTo[0] && parseFormattedDate(formattedDate) > parseFormattedDate(newTo[0])) {
          newTo[0] = formattedDate
          newFrom[0] = ""
          setClickCount(1)
          setLastClick({ date: formattedDate, timestamp: currentTime })
          updateDatesState(newFrom, newTo, false)
          setSelectedDates(newTo)
          return
        }
        newFrom[0] = formattedDate
      }
    }

    setClickCount(nextCount)
    setLastClick({ date: formattedDate, timestamp: currentTime })

    const isRoundTrip =
      newFrom[0] &&
      newTo[0] &&
      parseFormattedDate(newFrom[0]).getTime() !== parseFormattedDate(newTo[0]).getTime()
    updateDatesState(newFrom, newTo, isRoundTrip)

    setSelectedDates(isDeparture ? newFrom : newTo)
  }

  const updateDatesState = (newFrom, newTo, isRoundTrip) => {
    const hasTo = newTo[0] && newTo[0].trim() !== ""
    const hasFrom = newFrom[0] && newFrom[0].trim() !== ""

    dispatch(setAviaSearchParams({ key: "dates.from", value: newFrom }))
    dispatch(setAviaSearchParams({ key: "dates.to", value: newTo }))
    dispatch(setAviaSearchParams({ key: "dates.round_trip", value: hasFrom && hasTo }))
    dispatch(setAviaSearchParams({ key: "dates.one_way", value: hasFrom && !hasTo }))
  }

  const isDateSelected = (day, date) => {
    // Past dates should never appear selected
    if (isPastDate(day, date)) return false

    const formattedDate = formatDate(day, date)
    const dates = Array.isArray(selectedDates) ? selectedDates : []
    return dates.includes(formattedDate)
  }

  const isDateInRange = (day, date) => {
    if (mode !== DATES_FLEX_MODE) return false
    // Past dates should never appear in range
    if (isPastDate(day, date)) return false

    const formattedDate = formatDate(day, date)
    const dates = Array.isArray(selectedDates) ? selectedDates : []

    if (dates.length < 2) return false

    const sortedDates = [...dates].sort()
    const firstDate = sortedDates[0]
    const lastDate = sortedDates[sortedDates.length - 1]

    return (
      formattedDate !== firstDate && formattedDate !== lastDate && dates.includes(formattedDate)
    )
  }

  const renderCalendarHeader = (date, isSecondary = false, isLeft = true) => {
    return (
      <div className="flex items-center justify-between mb-3">
        {!isSecondary ? (
          <button
            onClick={() => changeMonth(-1)}
            className="p-1 hover:bg-gray-100 rounded cursor-pointer"
          >
            <ChevronLeft className="w-5 h-5 text-second-success" />
          </button>
        ) : (
          <div className="w-7"></div>
        )}

        <div className="relative">
          <button
            onClick={() => handleDateSelect(isLeft)}
            className={`flex items-center gap-2 px-3 py-0.5 hover:bg-gray-100 rounded font-medium cursor-pointer ${
              openMonthPicker === (isLeft ? "left" : "right") ? "shadow-lg" : ""
            }`}
          >
            {months[date.getMonth()]} {date.getFullYear()}
            <ChevronDown className="w-4 h-4" />
          </button>

          <AnimatePresence>
            {openMonthPicker === (isLeft ? "left" : "right") && (
              <motion.div
                ref={datePickerSect}
                className={`absolute top-full mt-1 bg-white rounded-lg shadow-lg z-10 py-2 w-48 h-40 overflow-y-auto ${
                  isLeft ? "left-0" : "right-0"
                }`}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {months.map((month, index) => (
                  <button
                    key={month}
                    onClick={() => selectMonth(index, isLeft)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 cursor-pointer hover:text-second-success transition-colors"
                  >
                    {month} {date.getFullYear()}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {isSecondary ? (
          <button
            onClick={() => changeMonth(1)}
            className="p-1 hover:bg-gray-100 rounded cursor-pointer"
          >
            <ChevronRight className="w-5 h-5 text-second-success" />
          </button>
        ) : (
          <div className="w-7"></div>
        )}
      </div>
    )
  }

  const renderWeekDays = () => {
    return (
      <div className="grid grid-cols-7 gap-1 relative">
        <div className="absolute bottom-1 left-0 right-0 border-b border-text-secondary/30"></div>

        {weekDays.map((day, idx) => (
          <div
            key={`weekday-${idx}`}
            className="h-10 flex items-center justify-center text-sm font-medium text-text-main"
          >
            {day}
          </div>
        ))}
      </div>
    )
  }

  const renderCalendarGrid = (date, isLeft) => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(date)
    const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`
    const highlighted = highlightedDates[monthKey] || []
    const days = []

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <motion.div
          key={`empty-${i}`}
          className="h-10 w-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2, delay: i * 0.01 }}
        />
      )
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const isHighlighted = highlighted.includes(day)
      const index = startingDayOfWeek + day - 1

      const dayString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`

      const isAvailable = isDeparture
        ? availableDepart.includes(dayString)
        : availableRetur.includes(dayString)

      const isPast = isPastDate(day, date)
      const isSelected = isDateSelected(day, date)
      const isInRange = isDateInRange(day, date)

      const dates = Array.isArray(selectedDates) ? selectedDates : []
      const sortedDates = [...dates].sort((a, b) => {
        const [dayA, monthA, yearA] = a.split(".").map(Number)
        const [dayB, monthB, yearB] = b.split(".").map(Number)

        const dateA = new Date(yearA, monthA - 1, dayA)
        const dateB = new Date(yearB, monthB - 1, dayB)

        return dateA - dateB
      })

      const isFirstDate = dates.length > 1 && formatDate(day, date) === sortedDates[0]
      const isLastDate =
        dates.length > 1 && formatDate(day, date) === sortedDates[sortedDates.length - 1]
      const isBoundaryDate = isFirstDate || isLastDate

      days.push(
        <motion.div
          key={`day-${day}-${index}`}
          onMouseEnter={() =>
            !isPast && isDeparture && mode === DATES_FLEX_MODE && handleFlexMouseEnter(day, date)
          }
          onClick={() => {
            if (!isPast) handleDay(day, date)
          }}
          className={cn(
            "h-10 w-10 flex rounded-md flex-col items-center justify-center text-sm transition-colors duration-300",
            // Past dates: dimmed, no interaction
            isPast ? "opacity-30 cursor-not-allowed" : "cursor-pointer",
            // Flex mode select-none
            !isPast && mode === DATES_FLEX_MODE && "select-none",
            // Highlight start date while selecting
            !isPast &&
              isSelecting &&
              isDeparture &&
              formatDate(day, date) === dragStartDate &&
              "ring-2 ring-second-success",
            // Selected boundary dates
            !isPast &&
              isBoundaryDate &&
              isSelected &&
              "bg-second-success hover:bg-success text-white",
            // In-range dates
            !isPast &&
              isInRange &&
              !isBoundaryDate &&
              "bg-second-success/30 hover:bg-success/30 text-black",
            // Single selected date
            !isPast &&
              isSelected &&
              !isInRange &&
              !isBoundaryDate &&
              dates.length === 1 &&
              "bg-second-success hover:bg-success text-white",
            // Available but not selected
            !isPast && !isSelected && !isInRange && isAvailable && "bg-green-500/55 text-black"
          )}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: isPast ? 0.3 : 1, scale: 1 }}
          whileHover={isPast ? {} : { scale: 1.05 }}
          whileTap={isPast ? {} : { scale: 0.95 }}
          transition={{
            duration: 0.2,
            delay: index * 0.01,
            type: "spring",
            stiffness: 300,
          }}
        >
          <div className="font-medium relative leading-tight">
            <span>{day}</span>
          </div>
        </motion.div>
      )
    }

    return <div className="grid grid-cols-7 gap-1">{days}</div>
  }

  const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)

  return (
    <div className="w-full overflow-hidden">
      <div className="flex gap-8">
        <div className="flex-1">
          {renderCalendarHeader(currentMonth, false, true)}
          {renderWeekDays()}

          <AnimatePresence mode="wait" initial={false} custom={direction}>
            <motion.div
              key={`${currentMonth.getMonth()}-${currentMonth.getFullYear()}`}
              custom={direction}
              initial={(custom) => ({
                opacity: 0,
                x: custom > 0 ? 300 : -300,
              })}
              animate={{
                opacity: 1,
                x: 0,
              }}
              exit={(custom) => ({
                opacity: 0,
                x: custom > 0 ? -300 : 300,
              })}
              transition={{
                duration: 0.4,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              {renderCalendarGrid(currentMonth, true)}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="h-6 w-px bg-second-success/30"></div>

        <div className="flex-1">
          {renderCalendarHeader(nextMonth, true, false)}
          {renderWeekDays()}

          <AnimatePresence mode="wait" initial={false} custom={direction}>
            <motion.div
              key={`${nextMonth.getMonth()}-${nextMonth.getFullYear()}`}
              custom={direction}
              initial={(custom) => ({
                opacity: 0,
                x: custom > 0 ? 300 : -300,
              })}
              animate={{
                opacity: 1,
                x: 0,
              }}
              exit={(custom) => ({
                opacity: 0,
                x: custom > 0 ? -300 : 300,
              })}
              transition={{
                duration: 0.4,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              {renderCalendarGrid(nextMonth, false)}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

export default DateModalCalendar
